import type { AnyAction, Middleware, MiddlewareAPI } from 'redux';

import { types as authTypes } from '../../actions/backendAuth';
import { types as callTypes } from '../../actions/backendCall';
import { DISMISS_LOW_CALL_VOLUME_REMINDER } from '../../actions/actionTypes/lowCallVolumeReminder';
import { showLowCallVolumeReminder, dismissLowCallVolumeReminder } from '../../actions/lowCallVolumeReminder';
import { SET_USER_INTERFACE_CONFIG } from '../../actions/actionTypes/trainer';
import { getLocalizationManager } from '../../../localization/utils';
import { reminderLogger } from '../../../utils/logger/loggerFactory';
import { requestUserAttention, clearUserAttention } from '../../../utils/attention';
import type { RootState } from '../../types';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;

// Test override: when true, forces the feature on without backend config and
// shrinks the cadence/window to 2 minutes for quick manual testing.
const TEST_MODE = false;
const TEST_PERIOD_MS = 2 * MINUTE_MS;

// Trigger logic (per-location config; sensible defaults):
//   Every `periodHours`, count recorded calls in the last `periodHours`.
//   If fewer than `thresholdCalls`, remind the agent.
const DEFAULT_THRESHOLD_CALLS = 7;
const DEFAULT_PERIOD_HOURS = 2;

// How often the ticker re-evaluates whether a checkpoint has been crossed.
const TICK_MS = TEST_MODE ? 10 * SECOND_MS : 60 * SECOND_MS;
// If the persisted heartbeat is older than this, the app was closed/asleep
// (not just idle-reloaded) → start a fresh session instead of resuming.
const OFFLINE_GAP_MS = TEST_MODE ? 45 * SECOND_MS : 10 * MINUTE_MS;
// Cap on retained call timestamps so the persisted array stays small.
const RETENTION_MS = 24 * HOUR_MS;

// localStorage keys — the schedule and call log must survive the app's idle
// auto-reloads (a multi-hour window cannot live in memory across reloads).
const LS_LOGIN_AT = '_lcvr_login_at';
const LS_LAST_CHECK_AT = '_lcvr_last_check_at';
const LS_CALLS = '_lcvr_calls';
const LS_LAST_SEEN = '_lcvr_last_seen';

interface ReminderConfig {
    enabled: boolean;
    thresholdCalls: number;
    periodMs: number; // drives both the check cadence and the counting window
    periodHours: number; // human-facing value shown in the reminder copy
}

const positiveNumberOr = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

const getConfig = (state: RootState): ReminderConfig => {
    const cfg = state.trainer?.userInterfaceConfig?.lowCallVolumeReminder;
    const periodHours = positiveNumberOr(cfg?.periodHours, DEFAULT_PERIOD_HOURS);
    return {
        enabled: TEST_MODE || Boolean(cfg?.enabled),
        thresholdCalls: positiveNumberOr(cfg?.thresholdCalls, DEFAULT_THRESHOLD_CALLS),
        // TEST_MODE shrinks the actual timing, but the copy still shows the
        // configured hours so it reads sensibly.
        periodMs: TEST_MODE ? TEST_PERIOD_MS : periodHours * HOUR_MS,
        periodHours
    };
};

// --- localStorage helpers (guarded; no-op when unavailable) ---
const lsGetNumber = (key: string): number | null => {
    try {
        if (typeof localStorage === 'undefined') return null;
        const raw = localStorage.getItem(key);
        if (raw == null) return null;
        const value = Number(raw);
        return Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
};

const lsSetNumber = (key: string, value: number) => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(key, String(value));
    } catch {
        // ignore
    }
};

const lsGetNumberArray = (key: string): number[] => {
    try {
        if (typeof localStorage === 'undefined') return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((n: unknown): n is number => typeof n === 'number' && Number.isFinite(n)) : [];
    } catch {
        return [];
    }
};

const lsSetNumberArray = (key: string, value: number[]) => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore
    }
};

const lsRemove = (key: string) => {
    try {
        if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    } catch {
        // ignore
    }
};

// --- In-memory state (the durable bits are mirrored in localStorage). ---
let loginAt: number | null = null;
let lastCheckAt: number | null = null;
let alertShown = false;
let monitorTimer: ReturnType<typeof setInterval> | undefined;
let initialized = false;

const countCallsInWindow = (now: number, windowMs: number): number => {
    const cutoff = now - windowMs;
    return lsGetNumberArray(LS_CALLS).filter(timestamp => timestamp > cutoff).length;
};

// Decide the session baseline once per page load: resume across a quick
// idle-reload, but start fresh if the app had actually been off for a while.
const ensureInitialized = () => {
    if (initialized) return;
    initialized = true;

    const now = Date.now();
    const persistedLogin = lsGetNumber(LS_LOGIN_AT);
    const lastSeen = lsGetNumber(LS_LAST_SEEN);
    const wasOffline = lastSeen == null || now - lastSeen > OFFLINE_GAP_MS;

    if (persistedLogin == null || wasOffline) {
        loginAt = now;
        lastCheckAt = null;
        lsSetNumber(LS_LOGIN_AT, now);
        lsRemove(LS_LAST_CHECK_AT);
        lsRemove(LS_CALLS);
    } else {
        loginAt = persistedLogin;
        lastCheckAt = lsGetNumber(LS_LAST_CHECK_AT);
    }
    alertShown = false;
};

const buildAttention = (state: RootState, periodHours: number) => {
    const localization = getLocalizationManager(state.user?.locale);
    return {
        title: localization.getText('trainer.lowCallVolumeReminder.title'),
        body: localization.getText('trainer.lowCallVolumeReminder.subtext', { hours: periodHours })
    };
};

// Show the reminder now, but only if still below threshold (count is re-read so
// a call that just ended is included).
const showReminder = (store: MiddlewareAPI, state: RootState, config: ReminderConfig) => {
    const callCount = countCallsInWindow(Date.now(), config.periodMs);
    if (callCount >= config.thresholdCalls) return;
    alertShown = true;
    store.dispatch(showLowCallVolumeReminder(callCount, config.periodHours));
    requestUserAttention(buildAttention(state, config.periodHours));
};

const runTick = (store: MiddlewareAPI) => {
    ensureInitialized();
    if (loginAt == null) return;

    const state = store.getState();
    const config = getConfig(state);
    if (!config.enabled) return;
    // Snoozed/paused has its own full-screen screen — don't compete with it.
    if (!state.trainer?.isTrainerActive) return;

    const now = Date.now();
    lsSetNumber(LS_LAST_SEEN, now); // heartbeat for offline-gap detection

    if (alertShown) return;

    // The most recent checkpoint since login that is now due.
    const elapsed = now - loginAt;

    // Simple countdown so you can watch how long until the next check (logged
    // every tick while the feature is enabled).
    const msToNextCheck = config.periodMs - (elapsed % config.periodMs);
    reminderLogger.log(`next check in ~${Math.ceil(msToNextCheck / 60000)} min`, {
        minutesLeft: Math.ceil(msToNextCheck / 60000),
        callsSoFar: countCallsInWindow(now, config.periodMs),
        thresholdCalls: config.thresholdCalls
    });

    if (elapsed < config.periodMs) return; // first check is one period after login
    const checkpoint = loginAt + Math.floor(elapsed / config.periodMs) * config.periodMs;
    if (checkpoint <= (lastCheckAt ?? loginAt)) return; // already checked this checkpoint

    lastCheckAt = checkpoint;
    lsSetNumber(LS_LAST_CHECK_AT, checkpoint);

    const callCount = countCallsInWindow(now, config.periodMs);
    const onCall = Boolean(state.trainer?.isStarted);
    const willShow = callCount < config.thresholdCalls && !onCall;
    reminderLogger.log('checkpoint reached', {
        callCount,
        thresholdCalls: config.thresholdCalls,
        periodHours: config.periodHours,
        onCall,
        willShow
    });

    if (callCount >= config.thresholdCalls) return;

    // Never show during a call. A call in progress (and the completed call it
    // becomes) is itself activity, so we skip this checkpoint entirely instead
    // of deferring it — the next reminder can only come one period later.
    if (onCall) return;
    showReminder(store, state, config);
};

const ensureMonitorTimer = (store: MiddlewareAPI) => {
    if (monitorTimer === undefined) {
        monitorTimer = setInterval(() => runTick(store), TICK_MS);
    }
};

const stopMonitoring = () => {
    if (monitorTimer !== undefined) {
        clearInterval(monitorTimer);
        monitorTimer = undefined;
    }
    clearUserAttention();
    loginAt = null;
    lastCheckAt = null;
    alertShown = false;
    initialized = false;
    lsRemove(LS_LOGIN_AT);
    lsRemove(LS_LAST_CHECK_AT);
    lsRemove(LS_CALLS);
    lsRemove(LS_LAST_SEEN);
};

const recordCall = () => {
    const now = Date.now();
    const cutoff = now - RETENTION_MS;
    const calls = lsGetNumberArray(LS_CALLS).filter(timestamp => timestamp > cutoff);
    calls.push(now);
    lsSetNumberArray(LS_CALLS, calls);
};

const lowCallVolumeReminder: Middleware = store => next => (action: AnyAction) => {
    const result = next(action);
    const typedStore = store as MiddlewareAPI;

    switch (action.type) {
        case authTypes.AUTH_SUCCESS:
            ensureInitialized();
            ensureMonitorTimer(typedStore);
            break;

        case authTypes.AUTH_FAIL:
            stopMonitoring();
            break;

        case SET_USER_INTERFACE_CONFIG: {
            // Confirms the per-location config actually arrived from the backend.
            const raw = typedStore.getState().trainer?.userInterfaceConfig?.lowCallVolumeReminder;
            const resolved = getConfig(typedStore.getState());
            reminderLogger.log('config received from backend', {
                raw,
                resolved: {
                    enabled: resolved.enabled,
                    thresholdCalls: resolved.thresholdCalls,
                    periodHours: resolved.periodHours
                }
            });
            break;
        }

        case callTypes.CALL_ENDED:
            // A completed (recorded) call is fresh activity — count it toward the
            // window. We intentionally do NOT raise a reminder here: a call that
            // just happened means the agent is active, so any reminder waits for
            // the next checkpoint.
            ensureInitialized();
            ensureMonitorTimer(typedStore);
            recordCall();
            break;

        case callTypes.CALL_STARTED:
            // A call is starting — never overlap it. Hide a visible reminder; it
            // won't reappear until the next checkpoint.
            if (alertShown) {
                typedStore.dispatch(dismissLowCallVolumeReminder('auto'));
            }
            break;

        case DISMISS_LOW_CALL_VOLUME_REMINDER:
            // Reset the in-memory guard so a later checkpoint can remind again.
            alertShown = false;
            clearUserAttention();
            break;

        default:
    }

    return result;
};

export default lowCallVolumeReminder;
